import { DestructiveConfirm } from "@pej/kore/components";
import type { ReactNode } from "react";

// Typed-name confirmation for catastrophic operations on the survey's own
// entities (survey / placement delete). Thin shim over kore's
// <DestructiveConfirm> — kore owns the alertdialog chrome, type-to-confirm
// friction, and disabled-until-match wiring; this wrapper just composes the
// title / echo copy. Ported from the vouchers admin's
// `components/common/irreversible-confirm-dialog.tsx`, narrowed to the one
// verb surveys needs and without the details-summary slot (surveys has
// nothing to show there).

export interface IrreversibleConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Action verb in title-case. */
    verb: "Delete";
    /** Entity noun lower-case, e.g. "survey", "placement". */
    entityName: string;
    /** Exact string the user must type to enable the confirm button. */
    matchValue: string;
    /** Human noun for the prompt — "name". */
    matchLabel: string;
    /** Cascade explanation copy shown beneath the title. */
    consequences: ReactNode;
    isPending: boolean;
    onConfirm: () => void;
}

export function IrreversibleConfirmDialog({
    open,
    onOpenChange,
    verb,
    entityName,
    matchValue,
    matchLabel,
    consequences,
    isPending,
    onConfirm,
}: IrreversibleConfirmDialogProps) {
    // "Deleting…" — match kore's `primaryLabel` slot, which stays static
    // across the in-flight state, so we swap the label here.
    const verbing = `${verb.endsWith("e") ? verb.slice(0, -1) : verb}ing…`;

    return (
        // `key={matchValue}` forces a remount when the target value changes so
        // the typed-input state clears between back-to-back open()s with
        // different entities (kore's DestructiveConfirm only resets on `open`
        // edges, not on expectedConfirmation changes mid-open).
        <DestructiveConfirm
            key={matchValue}
            open={open}
            onOpenChange={onOpenChange}
            title={`${verb} ${entityName}?`}
            description={consequences}
            expectedConfirmation={matchValue}
            echoLabel={`To confirm, type the ${matchLabel}`}
            primaryLabel={isPending ? verbing : verb}
            confirming={isPending}
            onConfirm={onConfirm}
        />
    );
}
